import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { CitationNodeView } from "./CitationNodeView";

export const CitationNode = Node.create({
  name: "citation",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      fileId: { default: null },
      fileName: { default: "" },
      pageNumber: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="citation"]',
        getAttrs: (element: HTMLElement) => {
          const page = element.getAttribute("data-page-number");
          const parsed = page ? parseInt(page, 10) : NaN;
          return {
            fileId: element.getAttribute("data-file-id"),
            fileName: element.getAttribute("data-file-name") ?? "",
            pageNumber: !isNaN(parsed) ? parsed : null,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs: Record<string, string> = {
      "data-type": "citation",
      "data-file-id": HTMLAttributes.fileId ?? "",
      "data-file-name": HTMLAttributes.fileName ?? "",
    };
    if (HTMLAttributes.pageNumber !== null && HTMLAttributes.pageNumber !== undefined) {
      attrs["data-page-number"] = String(HTMLAttributes.pageNumber);
    }
    return ["span", mergeAttributes(attrs)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CitationNodeView, { as: "span" });
  },

  addStorage() {
    return {
      markdown: {
        // Serialize Citation node back to [filename](ref:fileId#page=N) markdown
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        serialize(state: any, node: any) {
          const { fileId, fileName, pageNumber } = node.attrs;
          const ref =
            pageNumber !== null && pageNumber !== undefined
              ? `ref:${fileId}#page=${pageNumber}`
              : `ref:${fileId}`;
          state.write(`[${fileName}](${ref})`);
        },
        parse: {
          // After markdown-it renders HTML, replace <a href="ref:..."> with
          // <span data-type="citation"> so TipTap parses them as Citation nodes.
          updateDOM(element: HTMLElement) {
            element.querySelectorAll('a[href^="ref:"]').forEach((el) => {
              const anchor = el as HTMLAnchorElement;
              const href = anchor.getAttribute("href") ?? "";
              const withoutScheme = href.slice(4); // strip "ref:"
              const [uuidPart, hashPart] = withoutScheme.split("#");
              const parsedPage = hashPart?.startsWith("page=")
                ? parseInt(hashPart.slice(5), 10)
                : NaN;

              const span = document.createElement("span");
              span.setAttribute("data-type", "citation");
              span.setAttribute("data-file-id", uuidPart ?? "");
              span.setAttribute("data-file-name", anchor.textContent ?? "");
              if (!isNaN(parsedPage)) {
                span.setAttribute("data-page-number", String(parsedPage));
              }
              anchor.replaceWith(span);
            });
          },
        },
      },
    };
  },
});
